import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../Context/AuthContext';
import { authHeaders } from './APIService';

export type SocketEventHandlers = Record<string, (payload: never) => void>;

export const LIVE_UPDATES_UNAVAILABLE =
  'Live updates are unavailable. Feature flags and workflow status may be out of date.';

const RECOVERY_DELAY_MS = 2000;
const MAX_RECOVERY_DELAY_MS = 30000;
const AVAILABILITY_ATTEMPTS_THRESHOLD = 4;
// A refused handshake still fires 'connect' before the disconnect, so resetting attempts there would
// make the cap unreachable -- only a connection that survives this long counts as recovered.
// Known gap, accepted (UPDEV-7453 dev-testing): elapsed time, not a real server ack, so a refusal
// slower than this window can still reset attempts and briefly report available.
const STABLE_CONNECTION_MS = 3000;
// Past this many refusals the credential is the problem -- retry rarely rather than strand a recoverable socket.
const FAST_RECOVERY_REOPENS = 6;
const SLOW_RECOVERY_DELAY_MS = 300000;

export const useAuthenticatedSocket = (
  events: SocketEventHandlers,
  onAvailabilityChange?: (available: boolean) => void
): void => {
  const { token, provider } = useAuth();

  const eventsRef = useRef(events);
  const availabilityRef = useRef(onAvailabilityChange);
  useEffect(() => {
    eventsRef.current = events;
    availabilityRef.current = onAvailabilityChange;
  });

  useEffect(() => {
    if (!token) return;

    let socket: Socket | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stableTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let connectErrors = 0;
    let reopens = 0;
    let stopped = false;

    const open = () => {
      const opened = io(process.env.REACT_APP_API_SERVER_URL, {
        extraHeaders: authHeaders(token, provider)
      });
      socket = opened;

      // Dispatches via eventsRef so a handler that changes identity without a reconnect still updates --
      // but that only keeps values live. The key set itself is read once, here, at open().
      Object.keys(eventsRef.current).forEach((event) =>
        opened.on(event, ((payload: never) => eventsRef.current[event]?.(payload)) as Parameters<Socket['on']>[1])
      );

      opened.on('connect', () => {
        if (socket !== opened) return;
        stableTimer = setTimeout(() => {
          if (socket !== opened) return;
          // `reopens` is deliberately not reset here: recovery must not refill the fast-lane budget.
          attempts = 0;
          connectErrors = 0;
          availabilityRef.current?.(true);
        }, STABLE_CONNECTION_MS);
      });

      // socket.io retries a transport fault itself; this only counts it to report an outage, never reopens.
      opened.on('connect_error', () => {
        if (socket !== opened) return;
        connectErrors += 1;
        if (connectErrors === AVAILABILITY_ATTEMPTS_THRESHOLD) availabilityRef.current?.(false);
      });

      opened.on('disconnect', (reason) => {
        if (socket !== opened) return;
        // Any disconnect makes a pending stable-timer stale, even one socket.io will retry on its own.
        if (stableTimer) clearTimeout(stableTimer);
        if (reason === 'io server disconnect') recover();
      });
    };

    const recover = () => {
      if (stopped) return;
      if (stableTimer) clearTimeout(stableTimer);
      socket?.disconnect();
      socket = undefined;

      attempts += 1;
      if (attempts === AVAILABILITY_ATTEMPTS_THRESHOLD) availabilityRef.current?.(false);

      // Counted apart from `attempts`: a budget the stable-connection reset can refill is unreachable.
      reopens += 1;
      // Said once on entering the slow lane, because `attempts` may keep being reset below the threshold.
      if (reopens === FAST_RECOVERY_REOPENS + 1) availabilityRef.current?.(false);
      const delay =
        reopens > FAST_RECOVERY_REOPENS
          ? SLOW_RECOVERY_DELAY_MS
          : Math.min(RECOVERY_DELAY_MS * attempts, MAX_RECOVERY_DELAY_MS);
      retryTimer = setTimeout(() => {
        if (!stopped) open();
      }, delay);
    };

    open();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (stableTimer) clearTimeout(stableTimer);
      socket?.disconnect();
      socket = undefined;
    };
  }, [token, provider]);
};
